import React from 'react';
import {
  PlanContainer,
  SubtitleSection,
  SubtitleIcon,
  CurrentPlanInfo,
  RolloverSection,
  RolloverIcon,
  RolloverText,
  RolloverPages,
  SectionTitle,
  PlanCard,
  PlanHeader,
  PlanContent,
  InfoSection,
  InfoText,
  IconWrapper
} from './styles';
import {
  IconInfo,
  IconPaperCollection,
  IconPrinterInkDrop
} from '@veneer/core';
import PrinterDetails from './PrinterDetails';
import PlanCostDetails from './PlanCostDetails';
import { primitives } from '@veneer/primitives';
import { useData } from 'src/contexts/DataContext';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { useTranslationWithInterpolation } from 'src/hooks/useTranslationWithValues/useTranslationWithValuesHelper';
import { UpgradeCart } from 'src/types/graphql/product';
interface PlanSummaryProps {
  selectedPrinterDetails?: PrinterItem;
  currentPrinterDetails?: PrinterItem[];
  cartInfo?: UpgradeCart;
}

const PlanSummary = ({
  selectedPrinterDetails,
  currentPrinterDetails,
  cartInfo
}: PlanSummaryProps): JSX.Element => {
  const { rolloverData } = useData();
  const { getText, getTextWithValues } = useTranslationWithInterpolation();
  const color = primitives.color;
  const totalPagesApplicable = rolloverData?.totalPagesApplicable || 0;
  const totalPagesPrinted = rolloverData?.totalPagesPrinted || 0;
  const remainingPages = totalPagesApplicable - totalPagesPrinted;

  const recommendedInkPlan = selectedPrinterDetails?.inkPlan.find(
    (plan) => plan.recommendedPlan
  );
  const maxRollOverPages = recommendedInkPlan?.maxRolloverPages || 0;
  const remainingPagesLeft =
    remainingPages > maxRollOverPages ? maxRollOverPages : remainingPages;

  const planNote = getTextWithValues(
    'planSummary.currentPlan',
    'Your current plan is {{printerModel}} printer with {{pages}} pages/month.',
    {
      printerModel: currentPrinterDetails[0]?.printerName,
      pages: currentPrinterDetails[0]?.inkPlan[0]?.pagesPerMonth
    }
  );

  return (
    <PlanContainer>
      <SubtitleSection>
        <SubtitleIcon>
          <IconPrinterInkDrop size={20} />
        </SubtitleIcon>
        <div>
          <CurrentPlanInfo>{planNote}</CurrentPlanInfo>
          <CurrentPlanInfo>
            <b>
              {getTextWithValues(
                'planSummary.newPlanInfo',
                'Your new plan will be {{printerModel}} printer with {{pages}} pages/month',
                {
                  printerModel: selectedPrinterDetails?.printerName,
                  pages: recommendedInkPlan?.pagesPerMonth
                }
              )}
            </b>
          </CurrentPlanInfo>
        </div>
      </SubtitleSection>
      {remainingPagesLeft > 0 && (
        <RolloverSection>
          <RolloverIcon>
            <IconPaperCollection size={32}></IconPaperCollection>
          </RolloverIcon>
          <div>
            <RolloverText>
              {getText(
                'planSummary.rolloverText1',
                'Your rollover pages will be transferred automatically to your new plan, up to a cap of '
              )}
              {maxRollOverPages}
              {getText('planSummary.rolloverText2', ' pages')}
            </RolloverText>
            <RolloverPages>
              {getTextWithValues(
                'planSummary.rolloverPages',
                '{{remainingPagesLeft}} left / {{maxRollOverPages}}',
                {
                  remainingPagesLeft,
                  maxRollOverPages
                }
              )}
            </RolloverPages>
          </div>
        </RolloverSection>
      )}
      <PlanCard>
        <PlanHeader>{/* Card Header */}</PlanHeader>
        <PlanContent>
          <SectionTitle>
            {getText(
              'planSummary.selectedPlanTitle',
              'Your selected HP All-In Plan'
            )}
          </SectionTitle>
          <PrinterDetails selectedPrinterDetails={selectedPrinterDetails} />
          <InfoSection>
            <IconWrapper>
              <IconInfo
                filled
                color={color.electricBlue6}
                size={24}
              />
            </IconWrapper>
            <InfoText>
              {getText(
                'planSummary.adjustPlanInfo',
                'You can adjust your print plan once you have setup your new upgrade printer.'
              )}
            </InfoText>
          </InfoSection>
          <PlanCostDetails
            selectedPrinterDetails={selectedPrinterDetails}
            cartInfo={cartInfo}
          />
        </PlanContent>
      </PlanCard>
    </PlanContainer>
  );
};

export default PlanSummary;
